import {
  CalendarIcon,
  DialogueBubbleIcon,
  TimeIcon,
  replaceApiSyntaxWithVariables,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef } from 'react'
import { useVariables } from '../../../_components/variables-context'
import { useLocale } from 'src/hooks/use-locale'
import { getMessageTranslatedForUserLocale } from 'src/utils/locale.util'
import { getDateValuesFromSchedule } from '../messages/_components/programmed-message.helper'
import { EAgeGroup, TMessageContentRule } from '../messages/_components/programmed-message.type'
import { TTimelineMessageElement } from './followup-timeline.type'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { getDoctorName } from 'src/utils/string-formatter.util'

const TimelineMessageItemTooltip = ({
  timelineElement,
  correspondingMedEventName,
}: {
  timelineElement: TTimelineMessageElement
  correspondingMedEventName: string
}) => {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { variableStore, contentVariables, defaultContentVariable } = useVariables()
  const { activeDoctors } = useCurrentProject()
  const messageRefs = useRef<(HTMLParagraphElement | null)[]>([])

  const contents = timelineElement.contents
  const sortedRules = timelineElement.profileRules
    ? [...timelineElement.profileRules].sort((profileRule1, profileRule2) => {
        const rule1IsAll = profileRule1.medicalStaffMemberId === null && profileRule1.ageGroup === null
        const rule2IsAll = profileRule2.medicalStaffMemberId === null && profileRule2.ageGroup === null
        if (rule1IsAll && !rule2IsAll) return -1
        if (!rule1IsAll && rule2IsAll) return 1
        return 0
      })
    : undefined
  const hasRules = sortedRules && sortedRules.length > 0

  const getMessageHtml = (message: Partial<Record<string, string>>) =>
    replaceApiSyntaxWithVariables({
      messageContent: getMessageTranslatedForUserLocale({
        message,
        currentLocale: locale,
      }),
      variableStore,
      contentVariables,
      defaultContentVariable,
    })

  useEffect(() => {
    const messages = hasRules ? sortedRules.map((rule) => rule.message) : contents
    messages.forEach((message, i) => {
      const ref = messageRefs.current[i]
      if (ref) {
        const html = getMessageHtml(message)
        if (html.length) ref.replaceChildren(...html)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineElement.id])

  const getRuleSubtitle = (rule: TMessageContentRule): string => {
    const doctor = rule.medicalStaffMemberId
      ? activeDoctors.find((doctor) => String(doctor.id) === rule.medicalStaffMemberId)
      : null
    const doctorName = doctor ? getDoctorName(doctor) : null

    if (rule.ageGroup === EAgeGroup.MINOR && doctorName) {
      return t('pages.configuration.followups.rule-tooltip-subtitle-minor-doctor', { doctor: doctorName })
    }
    if (rule.ageGroup === EAgeGroup.MAJOR && doctorName) {
      return t('pages.configuration.followups.rule-tooltip-subtitle-adult-doctor', { doctor: doctorName })
    }
    if (rule.ageGroup === EAgeGroup.MINOR) {
      return t('pages.configuration.followups.rule-tooltip-subtitle-minor')
    }
    if (rule.ageGroup === EAgeGroup.MAJOR) {
      return t('pages.configuration.followups.rule-tooltip-subtitle-adult')
    }
    if (doctorName) {
      return t('pages.configuration.followups.rule-tooltip-subtitle-doctor', { doctor: doctorName })
    }
    return t('pages.configuration.followups.rule-tooltip-subtitle-all')
  }

  if (!hasRules && !contents.length) return null

  return (
    <span className="c-absolute c-shadow-xl c-left-[calc(100%+10px)] c-w-80 c-top-0 c-flex-col c-z-10 c-border c-border-grey-500 c-bg-white c-hidden group-hover:c-flex c-p-3 c-rounded-md c-text-black c-text-left">
      <h3 className="c-text-sm c-font-bold c-pb-1">{timelineElement.name}</h3>
      <hr className="c-mb-3 c-border-t-grey-300" />
      <ul>
        <li className="c-flex c-mb-4 c-font-bold c-items-center">
          <CalendarIcon className="c-w-5 c-mr-2" />
          {t('pages.configuration.followups.message-tooltip-date', {
            ...getDateValuesFromSchedule(timelineElement.schedule),
            medEventName: correspondingMedEventName || '',
          })}
        </li>
        {timelineElement.schedule.atTime && (
          <li className="c-flex c-mb-4 c-font-bold c-items-center">
            <TimeIcon className="c-w-5 c-mr-2" />
            {t('pages.configuration.followups.at-time', { time: timelineElement.schedule.atTime })}
          </li>
        )}
        <li>
          <div className="c-flex c-items-center c-pb-2">
            <DialogueBubbleIcon className="c-w-5 c-mr-2 c-shrink-0" />
            <div className="c-font-bold">
              {timelineElement.isEditable
                ? t('pages.configuration.followups.message')
                : t('pages.configuration.followups.message-preview')}
            </div>
          </div>
          <div className="c-pl-7 c-flex c-flex-col">
            {hasRules
              ? sortedRules.map((rule, i) => (
                  <div key={`${rule.medicalStaffMemberId}-${rule.ageGroup}`}>
                    {i > 0 && <div className="c-h-px c-bg-grey-200 c-my-2" />}
                    {timelineElement.isEditable && (
                      <div className="c-text-xs c-font-normal c-text-grey-500 c-mb-1">
                        {getRuleSubtitle(rule)}
                      </div>
                    )}
                    <p
                      className="c-m-0 c-font-nexa"
                      ref={(el) => {
                        messageRefs.current[i] = el
                      }}
                    />
                  </div>
                ))
              : contents.map((content, i) => (
                  <div key={Object.values(content).join('-')}>
                    {i > 0 && <div className="c-h-px c-bg-grey-200 c-my-2" />}
                    <p
                      className="c-m-0 c-font-nexa"
                      ref={(el) => {
                        messageRefs.current[i] = el
                      }}
                    />
                  </div>
                ))}
          </div>
        </li>
      </ul>
    </span>
  )
}

export { TimelineMessageItemTooltip }
